import {
  Box,
  Button,
  Dropdown,
  Input,
  Option,
  Popup,
  Table,
  TableRow,
  TextArea,
  TopBar,
} from '@sam/components';
import { TableHeader } from '@sam/components/src/Table/Table.types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Company,
  Customer,
  CustomerTaskPrice,
  DeliveryNote,
  DocumentBucket,
  InvoiceMailConfiguration,
  InvoicePosition,
  InvoiceState,
  Language,
  MailType,
  Offer,
  OfferType,
  Order,
  Period,
  Receipt,
  ReceiptType,
  Right,
  SchambeckDoc,
  Task,
  createNewCancelReceipt,
  createNewCreditNoteReceipt,
  downloadFile,
  generateDropdownOptions,
  generateNotification,
  getAllDeliveryNotesForProject,
  getCustomerById,
  getCustomerUserById,
  getDeliveryNoteDocument,
  getDocumentListForFolders,
  getErrorPatternTablesForInvoice,
  getInvoicePDFDocument,
  getOfferById,
  getOfficeById,
  getOrderById,
  getReceiptSAPExportFiles,
  getTimeTablesForInvoice,
  sendInvoiceMail,
  updateDeliveryNote,
  updateInvoice,
  updateInvoicePeriod,
  updateOrder,
  useData,
} from 'shared';
import { getCompanyById } from 'shared/src/company/Company.axios';
import { CustomerLocation } from 'shared/src/customerLocation/CustomerLocation.types';
import { NotificationType } from 'shared/src/notification/notification.types';
import { getProjectById } from 'shared/src/project/Project.axios';
import dayjs from 'shared/src/tools/Dayjs';
import { useUser } from '../../components/UserContext';
import { FileOverview } from '../../components/files/FileOverview';
import { IconComponent, IconType } from '../../components/icons/IconComponent';
import { MailPopup } from '../../components/mailPopup/MailPopup';
import { SaveButtons } from '../../components/saveButtons/SaveButtons';
import { preventSubmitOnEnter } from '../../utils/general.utils';
import {
  convertDeliveryNotesIntoInvoiceOverviewRows,
  convertInvoicePositionIntoTableEntries,
  generateEmptyInvoicePosition,
} from '../../utils/invoice/Invoice.utils';
import { useLockDate } from '../../utils/lockDate/useLockDate';
import { generateEmptyMailConfiguration } from '../../utils/mail/Mail.utils';
import {
  generateDropdownOptionsForCustomerUsers,
  generateDropdownOptionsForCustomers,
} from '../../utils/order/Order.utils';
import { combineStandardAndCustomerTasks } from '../../utils/task/task.utils';
import { isUserAllowedToDo } from '../../utils/user/User.utils';

export const InvoiceEdit: React.FC = () => {
  const { axios, user, updateFootnoteConfig } = useUser();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation<{ invoice?: Receipt }>();
  const [invoice, setInvoice] = useState<Receipt | undefined>(location.state?.invoice);
  const [order, setOrder] = useState<Order>();
  const [activeLockDate] = useLockDate(order?.companyId);
  const [customer, setCustomer] = useState<Customer>();
  const [company, setCompany] = useState<Company>();
  const [documentToShow, setDocumentToShow] = useState<string>();
  const [isEditMode, toggleEditMode] = useState<boolean>(false);
  const [attachements, setAttachements] = useState<SchambeckDoc[]>([]);

  const isReadOnly: boolean = useMemo(
    (): boolean => invoice?.invoiceState !== InvoiceState.CREATED,
    [invoice?.invoiceState],
  );
  const [offer, setOffer] = useState<Offer>();
  const [confirmationType, setconfirmationType] = useState<
    ReceiptType.CANCEL | ReceiptType.CREDIT_NOTE
  >();
  const [deliveryNotes, setDeliveryNotes] = useState<DeliveryNote[]>([]);
  const [isMailConfigOpen, toggleMailConfigOpen] = useState<boolean>(false);
  const [adminPopupType, setAdminPopupType] = useState<'state' | 'export'>();
  const [newInvoiceState, setNewInvoiceState] = useState<InvoiceState>();
  const [mailConfiguration, setMailConfiguration] = useState<InvoiceMailConfiguration>({
    ...generateEmptyMailConfiguration(),
    invoiceId: invoice?.id || '',
    type: MailType.INVOICE,
    userId: user.id,
    language: user.language || Language.DE,
  });

  const button = useRef<HTMLButtonElement>(null);
  const form = useRef<HTMLFormElement>(null);

  const { data: loadedCustomers } = useData('CUSTOMER_ALL', {
    config: { fallbackData: [] },
  });
  const { data: allStandardTasks } = useData('TASKS_ALL', {
    config: { fallbackData: [] },
  });
  const { data: loadedCustomerUsers } = useData('CUSTOMER_USERS_ALL', {
    config: { fallbackData: [] },
  });
  const { data: loadedCurrencies } = useData('CURRENCY_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: loadedOffices } = useData('OFFICES_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: loadedBusinessAreas } = useData('BUSINESSAREA_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });
  const { data: loadedCustomerLocations } = useData('CUSTOMER_LOCATION_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });

  const { data: loadedCustomerArticles } = useData('CUSTOMER_ARTICLE_ALL_ACTIVE', {
    config: { fallbackData: [] },
  });

  //Load all deliveryNotes for the project
  useEffect(() => {
    if (!axios || !invoice?.projectId) return;
    getAllDeliveryNotesForProject(axios, invoice?.projectId).then(setDeliveryNotes);
  }, [axios, invoice?.projectId]);

  /**
   * Helper tp download the deliveryNote as a pdf
   * @param note the note to load
   */
  const handleDeliveryNoteDownload = (note: DeliveryNote, preview?: boolean): void => {
    if (!order) return;
    getDeliveryNoteDocument(
      axios,
      `${order.customerId}/${order.id}/${t(`pages.deliveryNote.create.title${note.language}`)} ${
        note.numberRangeNumber
      }.pdf`,
    ).then((file) => {
      if (!file) return;
      if (preview) setDocumentToShow(URL.createObjectURL(file));
      else downloadFile(file, t('pages.deliveryNote.create.title'));
    });
  };

  /**
   * Helper to assign or unassign a note to the current invoice
   * @param note the note to update. Remove invoiceId if present or add it otherwise
   */
  const handleNoteAssign = (note: DeliveryNote): void => {
    if (!invoice) return;
    const updatedDeliveryNote: DeliveryNote = {
      ...note,
      invoiceId: !!note.invoiceId && note.invoiceId !== '' ? undefined : invoice.id,
    };
    updateDeliveryNote(axios, updatedDeliveryNote).then((updatedEntry) => {
      if (!updatedEntry) return;
      setDeliveryNotes((prev) =>
        prev.map((entry) => (note.id === entry.id ? updatedEntry : entry)),
      );
    });
  };
  /**
   * Holds all rows for deliveryNotes that are not assigned to an invoice
   */
  const unassignedDeliveryNoteRows: TableRow[] = useMemo((): TableRow[] => {
    if (!invoice || !order) return [];
    return convertDeliveryNotesIntoInvoiceOverviewRows({
      deliveryNotes,
      navigate,
      handleFileDownload: handleDeliveryNoteDownload,
      handleNoteAssign,
      assigned: false,
      invoiceId: invoice.id,
    });
  }, [axios, deliveryNotes, invoice, navigate, order, t]);

  /**
   * Holds all deliveryNotes that are assigned to the receipt
   */
  const assignedDeliveryNoteRows: TableRow[] = useMemo((): TableRow[] => {
    if (!invoice || !order) return [];
    return convertDeliveryNotesIntoInvoiceOverviewRows({
      deliveryNotes,
      navigate,
      handleFileDownload: handleDeliveryNoteDownload,
      handleNoteAssign,
      assigned: true,
      invoiceId: invoice.id,
    });
  }, [axios, deliveryNotes, invoice, navigate, order, t]);
  /**
   * DropdownOptions to choose a customer for a different invoiceRecipient
   */
  const customerDropdownOptions: Option[] = useMemo(
    (): Option[] => generateDropdownOptionsForCustomers(loadedCustomers),
    [loadedCustomers],
  );

  /**
   * DropdownOptions to choose a customerUser for a different invoiceRecipient
   */
  const customerUserDropdownOptions: Option[] = useMemo((): Option[] => {
    if (!order?.invoiceRecipient?.customerId) return [];

    return generateDropdownOptionsForCustomerUsers(
      loadedCustomerUsers,
      order.invoiceRecipient.customerId,
    );
  }, [loadedCustomerUsers, order?.invoiceRecipient?.customerId]);

  /**
   * Holds the options for the invoice recipient locations
   */
  const invoiceLocationOptions: Option[] = useMemo((): Option[] => {
    if (!order?.invoiceRecipient.customerId) return [];
    const locations: CustomerLocation[] = loadedCustomerLocations.filter(
      (location) => location.customerId === order?.invoiceRecipient.customerId,
    );
    return generateDropdownOptions(locations, 'name', 'id');
  }, [loadedCustomerLocations, order?.invoiceRecipient.customerId]);

  /**
   * Hook to update the footNote containg the create and update data
   */
  useEffect(() => {
    updateFootnoteConfig(invoice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice?.createDate]);

  /**
   * Helper method to update a position onChange
   * @param id of the position to update
   * @param key that should be updated
   * @param value of the updated key
   */
  const handlePositionUpdate = useCallback(
    (id: string, key: keyof InvoicePosition, value: string | number | Date): void => {
      if (!invoice) return;
      setInvoice({
        ...invoice,
        invoicePositions: invoice.invoicePositions.map((position) =>
          position.id === id ? { ...position, [key]: value } : position,
        ),
      });
    },
    [invoice],
  );

  /**
   * Util method to send an receipt per mail and update the invoiceState
   */
  const handleSendMail = async (): Promise<void> => {
    if (!invoice) return;

    return sendInvoiceMail(axios, mailConfiguration).then((updatedInvoice) => {
      if (updatedInvoice) {
        setInvoice(updatedInvoice);
        generateNotification({
          type: NotificationType.SUCCESS,
          value: t('general.notification.success.sendInvoiceMail'),
        });
      }
      toggleMailConfigOpen(false);
    });
  };

  /**
   * Util method to delete a position from the invoice
   * @param id of the position to delete
   */
  const handleDeletePosition = useCallback(
    (id: string): void => {
      if (!invoice) return;
      setInvoice({
        ...invoice,
        invoicePositions: invoice.invoicePositions.filter((position) => position.id !== id),
      });
    },
    [invoice],
  );

  /**
   * Holds the standard tasks and customerArticles converted to tasks
   */
  const availableTasks: Task[] = useMemo((): Task[] => {
    const configuredTasks: Task[] = [
      ...combineStandardAndCustomerTasks(
        allStandardTasks,
        loadedCustomerArticles,
        order?.customerId,
        order?.officeId,
        true,
      ),
    ];
    offer?.offeredTasks.forEach(
      (task) =>
        !configuredTasks.some((configuredTask) => configuredTask.id === task.id) &&
        configuredTasks.push(task.task),
    );
    return configuredTasks;
  }, [
    allStandardTasks,
    loadedCustomerArticles,
    order?.customerId,
    order?.officeId,
    offer?.offeredTasks,
  ]);

  /**
   * Helper method to handle the change of an article for a position
   * @param taskId the selected task
   * @param positionId the updated position
   */
  const handleArticleChange = useCallback(
    (taskId: string, positionId: string): void => {
      if (!invoice || !order) return;
      const selectedTask: Task | undefined = availableTasks.find((task) => task.id === taskId);
      if (!selectedTask) {
        generateNotification({
          type: NotificationType.ERROR,
          value: t('general.notification.error.addTasksToInvoice'),
        });
        return;
      }

      let price: number = selectedTask.price;
      const customerTaskPrice: CustomerTaskPrice | undefined = customer?.customPrices.find(
        (customerPrice) => customerPrice.taskId === selectedTask.id,
      );
      if (customerTaskPrice) price = customerTaskPrice.price;
      else if (selectedTask.officePrices?.get(invoice.officeId)?.get(order.currencyId))
        price = selectedTask.officePrices.get(invoice.officeId)!.get(order.currencyId)!;
      setInvoice({
        ...invoice,
        invoicePositions: invoice.invoicePositions.map((position) =>
          position.id === positionId
            ? {
                ...position,
                price,
                taskId,
                articleNumber: selectedTask.identifier,
                description: selectedTask.translations.get(offer?.language || Language.DE) || '',
                amount: undefined,
                unitCode: selectedTask.unitCode,
                customerArticle: !allStandardTasks.some((task) => task.id === taskId),
              }
            : position,
        ),
      });
    },
    [allStandardTasks, availableTasks, customer?.customPrices, invoice, offer?.language, order, t],
  );

  // TableRows for the different positions of the invoice
  const invoicePositionRows: TableRow[] = useMemo((): TableRow[] => {
    if (!invoice) return [];
    return convertInvoicePositionIntoTableEntries(
      invoice.invoicePositions,
      availableTasks,
      handleArticleChange,
      handlePositionUpdate,
      handleDeletePosition,
      isReadOnly && !isEditMode,
    );
  }, [
    availableTasks,
    handleArticleChange,
    handleDeletePosition,
    handlePositionUpdate,
    invoice,
    isEditMode,
    isReadOnly,
  ]);

  //Hook to load order, customer and company to be displayed
  useEffect(() => {
    if (!invoice) return;
    getOrderById(axios, invoice?.orderId).then((loadedOrder) => {
      setOrder(loadedOrder);
      if (loadedOrder?.customerId)
        Promise.all([
          getCustomerById(axios, loadedOrder.customerId),
          getCustomerUserById(axios, loadedOrder.invoiceRecipient.customerUserId),
          getCompanyById(axios, loadedOrder.companyId),
          getOfficeById(axios, loadedOrder.officeId),
          getProjectById(axios, invoice.projectId),
        ]).then(
          ([loadedCustomer, loadedInvoiceContact, loadedCompany, loadedOffice, loadedProject]) => {
            if (!loadedProject) return;
            setCustomer(loadedCustomer);
            setCompany(loadedCompany);
            const invoiceRecipientMail: string = loadedInvoiceContact?.contact.mail || '';

            getOfferById(axios, loadedProject.acceptedOfferId).then((loadedOffer) => {
              setOffer(loadedOffer);
              setMailConfiguration((old) => ({
                ...old,
                smtpConfig: loadedOffice!.accountingMailConfiguration,
                receiver: invoiceRecipientMail,
                language: loadedOffer?.language,
              }));
            });
          },
        );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoice]);

  /**
   * Util method to open the mailPopup and update the mailConfig with the neccessary informations and attachements
   */
  const handleOpenMailPopup = () => {
    if (!order || !invoice) return;
    const mailType: MailType =
      invoice?.receiptType === ReceiptType.INVOICE
        ? MailType.INVOICE
        : invoice?.receiptType === ReceiptType.CANCEL
          ? MailType.CANCEL_RECEIPT
          : MailType.CREDIT_NOTE;

    getDocumentListForFolders(axios, {
      path: `${order.customerId}/${order.id}/${invoice.id}/`,
      buckets: [DocumentBucket.INVOICE],
      includeNested: false,
    }).then((loadedSchambeckDocs) => {
      setAttachements(loadedSchambeckDocs);
      setMailConfiguration({
        ...mailConfiguration,
        attachements: [],
        invoiceId: invoice?.id || '',
        type: mailType,
        userId: user.id,
        language: offer?.language || Language.DE,
      });
    });
    toggleMailConfigOpen(true);
  };

  /**
   * Helper method to update or create a new Invoice
   * @param redirect decides if the method should navigate back after success
   */
  const handleSubmit = async (redirect: boolean): Promise<void> => {
    button.current?.click();
    if (!form.current?.checkValidity() || !invoice || !order) return Promise.resolve();

    if (invoice.id) {
      return Promise.all([updateInvoice(axios, invoice), updateOrder(axios, order)]).then(
        ([updatedInvoice, updateOrder]) => {
          if (redirect && updatedInvoice) navigate('/invoice');
          if (updatedInvoice && updateOrder) {
            setInvoice(updatedInvoice);
            setOrder(updateOrder);
            generateNotification({
              value: t('general.notification.success.saveSuccessfull'),
              type: NotificationType.SUCCESS,
            });
          }
        },
      );
    } else {
      generateNotification({
        type: NotificationType.INFO,
        value: t('general.notification.info.functionNotAvailable'),
      });
    }
  };

  /**
   * Util method to handle the change of a period and updates the invoice positions for the new protocols
   * @param period updated period of the invoice
   */
  const handlePeriodChange = (period: Period): void => {
    if (!invoice) return;
    if (dayjs(period.endDate).isBefore(period.startDate)) {
      generateNotification({
        type: NotificationType.ERROR,
        value: t('general.notification.error.unplausibleDates'),
      });
      return;
    } else if (
      dayjs(activeLockDate).isSameOrAfter(period.startDate) ||
      dayjs(activeLockDate).isSameOrAfter(period.endDate)
    ) {
      generateNotification({
        type: NotificationType.ERROR,
        value: t('general.notification.error.lockedByLockDate'),
      });
      return;
    }
    if (offer?.offerType === OfferType.CUSTOMER_PROJECT_COPY) return;
    updateInvoicePeriod(axios, { ...invoice, period }).then((updatedInvoice) =>
      setInvoice(updatedInvoice),
    );
  };

  /**
   * If nothing than the positions waas edited, a creaditNote is possible
   */
  const isCreditNotePossible: boolean = useMemo((): boolean => {
    const originalInvoice: Receipt | undefined = location.state?.invoice;

    if (!originalInvoice) return false;
    else if (
      dayjs(originalInvoice.shownPeriod?.startDate).isSame(
        invoice?.shownPeriod?.startDate,
        'date',
      ) &&
      dayjs(originalInvoice.shownPeriod?.endDate).isSame(invoice?.shownPeriod?.endDate, 'date')
    )
      return true;
    return false;
  }, [invoice?.shownPeriod?.endDate, invoice?.shownPeriod?.startDate, location.state?.invoice]);

  /**
   * helper method to download the correct protocol
   * @param type to decide what document to create
   */
  const getProtocolDocument = async (type: 'time' | 'pieces'): Promise<void> => {
    if (!invoice?.id) return;
    const document: Blob | undefined =
      type === 'time'
        ? await getTimeTablesForInvoice(axios, invoice.id)
        : await getErrorPatternTablesForInvoice(axios, invoice.id);
    if (document) setDocumentToShow(URL.createObjectURL(document));
  };
  /**
   *  Util method to create a cancelReceipt or creditNote
   */
  const handleCreateInvoiceCorrection = async (): Promise<void> => {
    if (!invoice || !confirmationType) return Promise.resolve();
    if (confirmationType === ReceiptType.CANCEL)
      return createNewCancelReceipt(axios, invoice).then(
        (createdReceipt) => createdReceipt && navigate('/invoice'),
      );
    else
      return createNewCreditNoteReceipt(axios, invoice).then(
        (createdReceipt) => createdReceipt && navigate('/invoice'),
      );
  };

  /**
   * Helper to update the invoiceState or trigger an export
   */
  const handleAdminPopupSubmit = (): void => {
    if (!invoice) return;
    if (adminPopupType === 'export')
      getReceiptSAPExportFiles(axios, [invoice.id]).then((file) => {
        if (!file) return;
        const filename: string = `${invoice.invoiceNumber} Export`;
        downloadFile(file, filename);
      });
    else if (newInvoiceState && adminPopupType === 'state') {
      setInvoice({ ...invoice, invoiceState: newInvoiceState });
      setNewInvoiceState(undefined);
    }
    setAdminPopupType(undefined);
  };

  return (
    <form ref={form} onSubmit={(evt) => evt.preventDefault()} onKeyDown={preventSubmitOnEnter}>
      <TopBar
        title={t(`pages.invoice.edit.topBarHeadline.${invoice?.receiptType}`, {
          replace: { invoiceNumber: invoice?.invoiceNumber || '' },
        })}
        onBackClick={() => navigate(-1)}
      >
        {isUserAllowedToDo(user.right, [Right.ALL]) && (
          <>
            <Button
              value={t('general.buttons.invoiceExport')}
              onClick={() => setAdminPopupType('export')}
            />
            <Button
              value={t('general.buttons.changeInvoiceState')}
              onClick={() => setAdminPopupType('state')}
            />
          </>
        )}
        {(invoice?.invoiceState === InvoiceState.PENDING ||
          invoice?.invoiceState === InvoiceState.EXPORTED) &&
          !isEditMode &&
          invoice.receiptType === ReceiptType.INVOICE && (
            <Button
              disabled={
                dayjs(invoice.invoiceDate).isBefore(activeLockDate) ||
                dayjs(invoice.invoiceDate).isSame(activeLockDate)
              }
              value={t('general.buttons.edit')}
              onClick={() => toggleEditMode(true)}
            />
          )}
        {isEditMode ? (
          <>
            <Button
              value={t('general.buttons.invoiceCancel')}
              onClick={() => {
                setMailConfiguration({
                  ...mailConfiguration,
                  type: MailType.CANCEL_RECEIPT,
                });
                setconfirmationType(ReceiptType.CANCEL);
              }}
            />
            <Button
              value={t('general.buttons.creditNote')}
              disabled={!isCreditNotePossible}
              onClick={() => {
                setMailConfiguration({
                  ...mailConfiguration,
                  type: MailType.CREDIT_NOTE,
                });
                setconfirmationType(ReceiptType.CREDIT_NOTE);
              }}
            />
            <Button
              value={t('general.buttons.cancel')}
              onClick={() => {
                setInvoice(location.state?.invoice);
                toggleEditMode(false);
              }}
            />
          </>
        ) : (
          <SaveButtons handleSubmit={handleSubmit} buttonRef={button} />
        )}
      </TopBar>

      <Popup
        title={t('pages.invoice.edit.adminPopup.title')}
        isOpen={!!adminPopupType}
        onClose={() => setAdminPopupType(undefined)}
        buttons={[
          <Button
            value={t('general.buttons.cancel')}
            onClick={() => setAdminPopupType(undefined)}
          />,
          <Button
            value={t('general.buttons.confirm')}
            onClick={handleAdminPopupSubmit}
            disabled={adminPopupType === 'state' && !newInvoiceState}
          />,
        ]}
      >
        {adminPopupType === 'state' ? (
          <div className="invoice-edit__admin-popup-content">
            <Dropdown
              label={t('pages.invoice.edit.adminPopup.state')}
              options={Object.values(InvoiceState).map((state) => ({
                label: t(`general.invoiceState.${state}`),
                value: state,
              }))}
              selectedOption={newInvoiceState}
              onChange={(selectedState) => setNewInvoiceState(selectedState as InvoiceState)}
            />
          </div>
        ) : (
          <p>{t('pages.invoice.edit.adminPopup.exportConfirmation')}</p>
        )}
      </Popup>
      <Popup
        isOpen={!!confirmationType}
        onClose={() => setconfirmationType(undefined)}
        buttons={[
          <Button
            value={t('general.buttons.cancel')}
            onClick={() => setconfirmationType(undefined)}
          />,
          <Button value={t('general.buttons.confirm')} onClick={handleCreateInvoiceCorrection} />,
        ]}
      >
        {t(`pages.invoice.edit.confirmation.${confirmationType}`)}
      </Popup>
      <MailPopup
        attachements={attachements}
        sendDates={invoice?.sendDates}
        sendMail={handleSendMail}
        isOpen={isMailConfigOpen}
        toggleOpen={toggleMailConfigOpen}
        updateConfig={(updatedConfig) =>
          setMailConfiguration((old) => ({ ...old, ...updatedConfig }))
        }
        mailConfig={mailConfiguration}
        referenceId={invoice?.id || ''}
        userId={user.id}
      />
      {documentToShow && (
        <Popup isOpen={!!documentToShow} onClose={() => setDocumentToShow(undefined)}>
          <object data={documentToShow} type={'application/pdf'} width="1000px" height="700px" />
        </Popup>
      )}
      <Box title={t('pages.invoice.edit.baseData')}>
        <Input
          type="text"
          label={t('pages.invoice.edit.numberRange')}
          value={invoice?.invoiceNumber}
          onChange={() => {}}
          disabled
        />
        <div className="three-columns">
          <Input
            type="text"
            label={t('pages.invoice.edit.customer')}
            value={customer?.name}
            onChange={() => {}}
            disabled
          />
          <Input
            type="text"
            value={company?.name}
            label={t('pages.invoice.edit.company')}
            onChange={() => {}}
            disabled
          />
          <Input
            label={t('pages.invoice.edit.createDate')}
            value={invoice?.createDate}
            type="date"
            disabled
            onChangeDate={() => {}}
          />
          <Input
            label={t('pages.invoice.edit.invoiceDate')}
            value={invoice?.invoiceDate}
            type="date"
            disabled={isReadOnly}
            onChangeDate={(invoiceDate) => {
              if (!invoice || !invoiceDate) return;
              setInvoice({
                ...invoice,
                invoiceDate,
                dueDate: dayjs(invoiceDate)
                  .add(order?.paymentCondition?.daysToPay || 0, 'day')
                  .toDate(),
              });
            }}
          />
          <Input
            label={t('pages.invoice.edit.periodStart')}
            value={invoice?.period.startDate}
            type="date"
            disabled={isReadOnly}
            onChangeDate={(startDate) =>
              startDate &&
              invoice &&
              handlePeriodChange({
                startDate,
                endDate: invoice.period.endDate,
              })
            }
          />
          <Input
            label={t('pages.invoice.edit.periodEnd')}
            value={invoice?.period.endDate}
            type="date"
            disabled={isReadOnly}
            onChangeDate={(endDate) =>
              endDate &&
              invoice &&
              handlePeriodChange({
                endDate,
                startDate: invoice.period.startDate,
              })
            }
          />
          <Input
            label={t('pages.invoice.edit.deliveryDate')}
            value={invoice?.deliveryDate}
            type="date"
            disabled={isReadOnly}
            onChangeDate={(deliveryDate) =>
              invoice && deliveryDate && setInvoice({ ...invoice, deliveryDate })
            }
          />
          <Input
            label={t('pages.invoice.edit.shownPeriodStart')}
            value={invoice?.shownPeriod?.startDate}
            type="date"
            disabled={isReadOnly && !isEditMode}
            onChangeDate={(startDate) =>
              startDate &&
              invoice &&
              setInvoice({
                ...invoice,
                shownPeriod: { ...invoice.shownPeriod, startDate },
              })
            }
          />
          <Input
            label={t('pages.invoice.edit.shownPeriodEnd')}
            value={invoice?.shownPeriod?.endDate}
            type="date"
            disabled={isReadOnly && !isEditMode}
            onChangeDate={(endDate) =>
              endDate &&
              invoice &&
              setInvoice({
                ...invoice,
                shownPeriod: { ...invoice.shownPeriod, endDate },
              })
            }
          />
          {order && invoice && (
            <>
              <Dropdown
                disabled={isReadOnly}
                selectedOption={invoice.recipient?.customerId}
                label={t('pages.invoice.edit.invoiceRecipient')}
                options={customerDropdownOptions}
                onChange={(customerId) =>
                  setInvoice({
                    ...invoice,
                    recipient: {
                      customerUserId: '',
                      customerLocationId: '',
                      customerId,
                    },
                  })
                }
              />
              <Dropdown
                disabled={isReadOnly || !invoice.recipient.customerId}
                selectedOption={invoice.recipient?.customerLocationId}
                label={t('pages.project.edit.invoiceRecipientLocation')}
                options={invoiceLocationOptions}
                onChange={(customerLocationId) =>
                  setInvoice({
                    ...invoice,
                    recipient: {
                      customerId: invoice.recipient.customerId,
                      customerLocationId,
                      customerUserId: '',
                    },
                  })
                }
              />
              <Dropdown
                disabled={!invoice.recipient?.customerLocationId || isReadOnly}
                selectedOption={invoice.recipient?.customerUserId}
                label={t('pages.project.edit.invoiceContact')}
                options={customerUserDropdownOptions}
                onChange={(customerUserId) =>
                  setInvoice({
                    ...invoice,
                    recipient: {
                      ...invoice.recipient,
                      customerUserId,
                    },
                  })
                }
              />
            </>
          )}
        </div>
      </Box>
      {invoice && (
        <Box title={t('pages.invoice.edit.furtherInformation')}>
          <TextArea
            resizable
            onChange={(description) => setInvoice({ ...invoice, description })}
            value={invoice.orderDetails}
            label={t('pages.invoice.edit.description')}
            disabled={isReadOnly}
          />
          <TextArea
            disabled={isReadOnly}
            resizable
            onChange={(orderDetails) => setInvoice({ ...invoice, orderDetails })}
            value={invoice.orderDetails}
            label={t('pages.invoice.edit.orderDetails')}
          />
          <div className="three-columns">
            <Input
              type="text"
              onChange={(customerOrderNumber) => setInvoice({ ...invoice, customerOrderNumber })}
              value={invoice.customerOrderNumber}
              label={t('pages.invoice.edit.orderNumber')}
              disabled={isReadOnly}
            />
            <Dropdown
              disabled
              label={t('pages.invoice.edit.currency')}
              selectedOption={order?.currencyId}
              onChange={() => {}}
              options={generateDropdownOptions(loadedCurrencies, 'title', 'id')}
            />
            <Dropdown
              disabled
              onChange={() => {}}
              label={t('pages.invoice.edit.office')}
              selectedOption={order?.officeId}
              options={generateDropdownOptions(loadedOffices, 'name', 'id')}
            />
            <Dropdown
              disabled
              onChange={() => {}}
              label={t('pages.invoice.edit.businessArea')}
              selectedOption={order?.businessAreaId}
              options={generateDropdownOptions(loadedBusinessAreas, 'name', 'id')}
            />
            <Dropdown
              disabled
              onChange={() => {}}
              label={t('pages.invoice.edit.workingLocationCustomer')}
              selectedOption={order?.workingLocation.customerId}
              options={generateDropdownOptions(loadedCustomers, 'name', 'id')}
            />
            <Dropdown
              disabled
              onChange={() => {}}
              label={t('pages.invoice.edit.workingLocation')}
              selectedOption={order?.workingLocation.customerLocationId}
              options={generateDropdownOptions(loadedCustomerLocations, 'name', 'id')}
            />
          </div>
        </Box>
      )}
      <Box
        title={
          <div className="box-title-wrapper">
            <p className="box__title">{t('pages.invoice.edit.invoicePositions')}</p>
            <div className="box-title-wrapper__icon-wrapper">
              {!isReadOnly && (
                <IconComponent
                  type={IconType.ADD}
                  className="box-title-wrapper__icon-wrapper__icon"
                  onClick={() => {
                    if (!invoice) return;
                    setInvoice({
                      ...invoice,
                      invoicePositions: [
                        ...invoice.invoicePositions,
                        generateEmptyInvoicePosition({
                          index: invoice.invoicePositions.length + 1,
                          amount: undefined,
                        }),
                      ],
                    });
                  }}
                />
              )}
            </div>
          </div>
        }
      >
        <Table
          rows={invoicePositionRows}
          header={
            t('pages.invoice.edit.positionHeaders', {
              returnObjects: true,
            }) as TableHeader[]
          }
        />
      </Box>
      <Box title={t('pages.invoice.edit.documentTitle')}>
        {invoice?.id && order && (
          <FileOverview
            bucket={DocumentBucket.INVOICE}
            path={`${order.customerId}/${order.id}/${invoice.id}/`}
            userId={user.id}
          />
        )}
        <div className="four-columns">
          <Button
            value={t('pages.invoice.edit.pdfPreview')}
            onClick={() =>
              invoice &&
              getInvoicePDFDocument(axios, invoice.id, user.id).then(
                (document) => document && setDocumentToShow(URL.createObjectURL(document)),
              )
            }
          />
          <Button
            disabled={order?.withoutInvoice}
            value={t('general.buttons.send')}
            onClick={handleOpenMailPopup}
          />
          <Button value={t('general.buttons.times')} onClick={() => getProtocolDocument('time')} />
          <Button
            value={t('general.buttons.pieces')}
            onClick={() => getProtocolDocument('pieces')}
          />
        </div>
      </Box>
      <Box title={t('pages.invoice.edit.deliveryNotes')}>
        <p>{t('pages.invoice.edit.assigned')}</p>
        <Table
          rows={assignedDeliveryNoteRows}
          header={
            t('pages.invoice.edit.deliveryNoteTableHeader', {
              returnObjects: true,
            }) as TableHeader[]
          }
        />
        <p>{t('pages.invoice.edit.notAssigned')}</p>
        <Table
          rows={unassignedDeliveryNoteRows}
          header={
            t('pages.invoice.edit.deliveryNoteTableHeader', {
              returnObjects: true,
            }) as TableHeader[]
          }
        />
      </Box>
    </form>
  );
};
